import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Brand } from "../Brand";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Checkbox } from "../ui/checkbox";
import { useAuth } from "../../lib/auth";
import api from "../../lib/api";
import { toast } from "sonner";
import { UserPlus } from "lucide-react";

// Full ISO2 → display name map. The actual selectable list is the intersection of
// permitted residency + payment-partner residency, served by /api/config/countries.
const COUNTRY_NAMES = {
  AT: "Austria", BE: "Belgium", BG: "Bulgaria", HR: "Croatia", CY: "Cyprus",
  CZ: "Czech Republic", DK: "Denmark", EE: "Estonia", FI: "Finland", FR: "France",
  DE: "Germany", GR: "Greece", HU: "Hungary", IE: "Ireland", IT: "Italy",
  LV: "Latvia", LT: "Lithuania", LU: "Luxembourg", MT: "Malta", NL: "Netherlands",
  PL: "Poland", PT: "Portugal", RO: "Romania", SK: "Slovakia", SI: "Slovenia",
  ES: "Spain", SE: "Sweden", CA: "Canada", US: "United States", GB: "United Kingdom",
  CH: "Switzerland", NO: "Norway", IS: "Iceland", AU: "Australia", NZ: "New Zealand",
  JP: "Japan", SG: "Singapore",
};

export default function RegisterPage() {
  const navigate = useNavigate();
  const { signup } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", password: "", confirm: "", country: "", dob: "" });
  const [terms, setTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [countries, setCountries] = useState([]);

  useEffect(() => {
    api.get("/config/countries").then((r) => {
      const list = (r.data.allowlist || []).map((c) => ({
        code: c, name: COUNTRY_NAMES[c] || c,
      })).sort((a, b) => a.name.localeCompare(b.name));
      setCountries(list);
      // Default to first entry if customer is in EU; CY otherwise
      const def = list.find((c) => c.code === "CY") || list[0];
      if (def) setForm((f) => ({ ...f, country: def.code }));
    }).catch(() => {
      // Fallback to a minimal conservative list
      setCountries([{ code: "CY", name: "Cyprus" }, { code: "DE", name: "Germany" }]);
      setForm((f) => ({ ...f, country: "CY" }));
    });
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) { toast.error("Passwords do not match"); return; }
    if (!terms) { toast.error("Please accept the terms"); return; }
    if (!form.dob) { toast.error("Please enter your date of birth"); return; }
    setLoading(true);
    try {
      const user = await signup({
        name: form.name, email: form.email, password: form.password,
        country: form.country, dob: form.dob,
      });
      toast.success("Account created — welcome!");
      if (user?.sof_status === "required") {
        toast.info("As you're 65+, please complete your Source-of-Funds declaration before your first order.");
        navigate("/account/sof", { replace: true });
      } else {
        navigate("/account", { replace: true });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Signup failed");
    } finally { setLoading(false); }
  };

  return (
    <div
      className="min-h-screen xch-app-bg flex items-start sm:items-center justify-center p-4 py-6 sm:py-10 overflow-y-auto"
      style={{ minHeight: "100dvh", paddingBottom: "max(env(safe-area-inset-bottom), 6rem)" }}
      data-testid="register-page"
    >
      <div className="w-full max-w-md">
        <button onClick={() => navigate("/")} className="block mx-auto mb-6">
          <Brand size={28} />
        </button>
        <div className="xch-card p-7 space-y-5" data-testid="register-card">
          <div className="text-center space-y-1.5">
            <div className="xch-eyebrow"><UserPlus className="w-3 h-3" /> Get started</div>
            <h1 className="xch-headline text-3xl">Create your account</h1>
            <p className="text-sm text-muted-foreground">Free. Takes 30 seconds. €150 limit until verified.</p>
          </div>
          <form onSubmit={submit} className="space-y-3">
            <Field label="Full name">
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                     className="rounded-xl" data-testid="register-name-input" />
            </Field>
            <Field label="Email">
              <Input required type="email" value={form.email} autoComplete="email"
                     onChange={(e) => setForm({ ...form, email: e.target.value })}
                     className="rounded-xl" data-testid="register-email-input" />
            </Field>
            <Field label="Country of residence">
              <Select value={form.country} onValueChange={(v) => setForm({ ...form, country: v })}>
                <SelectTrigger className="rounded-xl" data-testid="register-country-select"><SelectValue placeholder="Select country" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {countries.map((c) => (
                    <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-muted-foreground mt-1">
                Country list reflects our regulatory permissions.
              </p>
            </Field>
            <Field label="Date of birth">
              <Input required type="date"
                     max={new Date(Date.now() - 18 * 365.25 * 86400e3).toISOString().slice(0, 10)}
                     value={form.dob}
                     onChange={(e) => setForm({ ...form, dob: e.target.value })}
                     className="rounded-xl font-mono" data-testid="register-dob-input" />
              <p className="text-[10px] text-muted-foreground mt-1">
                You must be 18 to use Xchangely (and up to 80). Customers 65 and over will be asked for a Source-of-Funds declaration before their first order.
              </p>
            </Field>
            <Field label="Password">
              <Input required type="password" minLength={6} value={form.password} autoComplete="new-password"
                     onChange={(e) => setForm({ ...form, password: e.target.value })}
                     className="rounded-xl" data-testid="register-password-input" />
            </Field>
            <Field label="Confirm password">
              <Input required type="password" minLength={6} value={form.confirm} autoComplete="new-password"
                     onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                     className="rounded-xl" data-testid="register-confirm-input" />
            </Field>
            <label className="flex items-start gap-2 text-xs text-muted-foreground" data-testid="register-terms-label">
              <Checkbox checked={terms} onCheckedChange={setTerms} data-testid="register-terms-checkbox"
                        className="mt-0.5" />
              <span>I agree to Xchangely's <a href="#" className="text-primary hover:underline">Terms</a> and <a href="#" className="text-primary hover:underline">Privacy policy</a>.</span>
            </label>
            <Button type="submit" disabled={loading || !terms}
                    className="w-full bg-primary hover:bg-primary/90 text-white rounded-xl h-12 font-display font-semibold"
                    data-testid="register-submit-btn">
              {loading ? "Creating…" : "Create account"}
            </Button>
          </form>
          <div className="text-center text-sm text-muted-foreground">
            Already have an account?{" "}
            <Link to="/login" className="text-primary font-semibold hover:underline" data-testid="register-login-link">
              Sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</Label>
    {children}
  </div>
);
